import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store/appStore';
import Terminal from '../components/Terminal';
import CommentManager from '../components/CommentManager';
import UserManager from '../components/UserManager';
import { Play, Loader, Home, Zap, Users, MessageSquare, Link2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePageSEO } from '../hooks/useSEO';

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    postUrl,
    setPostUrl,
    selectedServices,
    setSelectedServices,
    comments,
    users,
    isProcessing,
    setIsProcessing,
    addTerminalLine,
    clearTerminal,
    language,
  } = useAppStore();

  usePageSEO('dashboard', language);

  const [activeTab, setActiveTab] = useState('setup');

  const services = [
    { id: 'comments', label: t('dashboard.comments'), icon: '💬', color: 'from-blue-500 to-blue-600' },
    { id: 'likes', label: t('dashboard.likes'), icon: '❤️', color: 'from-red-500 to-red-600' },
    { id: 'views', label: t('dashboard.views'), icon: '👁️', color: 'from-purple-500 to-purple-600' },
    { id: 'shares', label: t('dashboard.shares'), icon: '🔗', color: 'from-green-500 to-green-600' },
  ];

  const toggleService = (serviceId) => {
    setSelectedServices(
      selectedServices.includes(serviceId)
        ? selectedServices.filter(s => s !== serviceId)
        : [...selectedServices, serviceId]
    );
  };

  const handleStartProcess = async () => {
    if (!postUrl.trim()) {
      alert(t('dashboard.postUrl'));
      return;
    }

    if (selectedServices.length === 0) {
      alert(t('dashboard.selectServices'));
      return;
    }

    setIsProcessing(true);
    clearTerminal();

    const commands = [
      { type: 'command', content: 'socialboost --init' },
      { type: 'output', content: 'Initializing SocialBoost Platform...' },
      { type: 'output', content: `Target URL: ${postUrl}` },
    ];

    selectedServices.forEach((service) => {
      commands.push({
        type: 'output',
        content: `Loading ${service} module...`,
      });
    });

    if (comments.length > 0) {
      commands.push({
        type: 'command',
        content: `socialboost --add-comments ${comments.length}`,
      });
      commands.push({
        type: 'output',
        content: `Adding ${comments.length} comments...`,
      });
      comments.forEach((comment, idx) => {
        commands.push({
          type: 'output',
          content: `[${idx + 1}/${comments.length}] @${comment.userName}: "${comment.text.substring(0, 30)}..."`,
        });
      });
    }

    if (users.length > 0) {
      commands.push({
        type: 'command',
        content: `socialboost --add-users ${users.length}`,
      });
      commands.push({
        type: 'output',
        content: `Registering ${users.length} users...`,
      });
      users.forEach((user, idx) => {
        commands.push({
          type: 'output',
          content: `[${idx + 1}/${users.length}] User: ${user.name}`,
        });
      });
    }

    commands.push({
      type: 'output',
      content: 'Connecting to social media platform...',
    });

    commands.push({
      type: 'command',
      content: 'socialboost --execute',
    });

    commands.push({
      type: 'output',
      content: 'Processing requests...',
    });

    for (let i = 0; i < commands.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 300));
      addTerminalLine(commands[i]);
    }

    await new Promise(resolve => setTimeout(resolve, 1000));

    addTerminalLine({
      type: 'success',
      content: 'Process completed successfully!',
    });

    setIsProcessing(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-light via-light to-light/95 pt-8 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-12 animate-slide-up">
          <div>
            <h1 className="text-5xl font-black text-maroon mb-2 gradient-text">
              {t('dashboard.title')}
            </h1>
            <p className="text-lg text-dark/70">
              {t('dashboard.subtitle')}
            </p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 bg-gradient-to-r from-maroon to-primary hover:shadow-lg text-light font-semibold py-3 px-6 rounded-lg transition transform hover:scale-105"
          >
            <Home size={20} />
            {t('dashboard.homeButton')}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div className="flex gap-2 bg-light rounded-t-2xl p-2 border-b-4 border-maroon shadow-lg">
              {[
                { id: 'setup', label: t('dashboard.title'), icon: Zap },
                { id: 'comments', label: t('dashboard.commentsList'), icon: MessageSquare },
                { id: 'users', label: t('dashboard.customizeNames'), icon: Users },
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-3 font-bold rounded-lg transition transform hover:scale-105 ${
                      activeTab === tab.id
                        ? 'text-light bg-gradient-to-r from-maroon to-primary shadow-lg'
                        : 'text-dark/70 hover:text-maroon'
                    }`}
                  >
                    <Icon size={18} />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Setup Tab */}
            {activeTab === 'setup' && (
              <div className="space-y-6 bg-light border-2 border-primary/30 rounded-2xl p-8 shadow-lg animate-slide-up">
                {/* URL Input */}
                <div>
                  <label className="block text-sm font-bold text-maroon mb-3 flex items-center gap-2">
                    <Link2 size={18} />
                    {t('dashboard.postUrl')}
                  </label>
                  <input
                    type="url"
                    value={postUrl}
                    onChange={(e) => setPostUrl(e.target.value)}
                    placeholder="https://www.instagram.com/p/..."
                    className="w-full bg-light border-2 border-primary/30 rounded-xl p-4 text-dark placeholder-dark/50 focus:outline-none focus:border-maroon transition text-lg"
                  />
                  <p className="text-sm text-dark/50 mt-2">
                    {t('dashboard.supportsAllPlatforms')}
                  </p>
                </div>

                {/* Services Selection */}
                <div>
                  <label className="block text-sm font-bold text-maroon mb-4 flex items-center gap-2">
                    <Zap size={18} />
                    {t('dashboard.selectServices')}
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    {services.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => toggleService(service.id)}
                        className={`group relative overflow-hidden p-6 rounded-2xl border-2 transition transform hover:scale-105 ${
                          selectedServices.includes(service.id)
                            ? `border-maroon bg-gradient-to-br ${service.color} shadow-lg`
                            : 'border-primary/30 hover:border-maroon/50 bg-light'
                        }`}
                      >
                        <div className={`text-4xl mb-2 transition transform group-hover:scale-125`}>
                          {service.icon}
                        </div>
                        <div className={`text-sm font-bold ${
                          selectedServices.includes(service.id)
                            ? 'text-light'
                            : 'text-dark'
                        }`}>
                          {service.label}
                        </div>
                        {selectedServices.includes(service.id) && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-light rounded-full flex items-center justify-center">
                            <span className="text-maroon font-bold">✓</span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-maroon/5 rounded-xl border border-maroon/20">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-maroon">{comments.length}</div>
                    <p className="text-xs text-dark/60">{t('dashboard.statsComments')}</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-maroon">{users.length}</div>
                    <p className="text-xs text-dark/60">{t('dashboard.statsAccounts')}</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-maroon">{selectedServices.length}</div>
                    <p className="text-xs text-dark/60">{t('dashboard.statsServices')}</p>
                  </div>
                </div>

                {/* Start Button */}
                <button
                  onClick={handleStartProcess}
                  disabled={isProcessing}
                  className={`w-full flex items-center justify-center gap-3 py-4 px-6 rounded-xl font-bold text-lg transition transform hover:scale-105 ${
                    isProcessing
                      ? 'bg-primary/50 text-light cursor-not-allowed'
                      : 'bg-gradient-to-r from-maroon to-primary hover:shadow-2xl text-light'
                  }`}
                >
                  {isProcessing ? (
                    <>
                      <Loader size={24} className="animate-spin" />
                      {t('dashboard.processing')}
                    </>
                  ) : (
                    <>
                      <Play size={24} />
                      {t('dashboard.startProcess')}
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Comments Tab */}
            {activeTab === 'comments' && (
              <div className="bg-light border-2 border-primary/30 rounded-2xl p-8 shadow-lg animate-slide-up">
                <CommentManager />
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="bg-light border-2 border-primary/30 rounded-2xl p-8 shadow-lg animate-slide-up">
                <UserManager />
              </div>
            )}
          </div>

          {/* Right Panel - Terminal */}
          <div className="lg:col-span-1">
            <div className="bg-light border-2 border-primary/30 rounded-2xl p-6 sticky top-24 shadow-lg animate-slide-up">
              <Terminal />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
